import React, { useEffect } from 'react';
import { motion } from 'motion/react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@/data/store/utils/redux';
import { getCurrentSessionUser } from '@/data/store/selectors';
import { updateSelfAsync, getSelfAsync } from '@/data/store/actions';
import { TUserSelfPayload } from '@/data/types';
import ProfileHeader from './components/ProfileHeader';
import ProfileContent from './components/ProfileContent';

// Updated interface to match API structure
export interface UserProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  username: string | null;
  avatar: string;
  image_url: string | null;
  isRegistered: boolean;
}

const Profile: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentUser = useSelector(getCurrentSessionUser);

  // Convert API user data to our UserProfile interface
  const userProfile: UserProfile | null = currentUser
    ? {
        id: currentUser.id,
        first_name: currentUser.first_name,
        last_name: currentUser.last_name,
        email: currentUser.email,
        phone: currentUser.phone,
        username: currentUser.username,
        avatar: currentUser.avatar,
        image_url: currentUser.image_url,
        isRegistered: currentUser.isRegistered,
      }
    : null;

  // Fetch user data on component mount
  useEffect(() => {
    if (!currentUser) {
      dispatch(getSelfAsync());
    }
  }, [dispatch, currentUser]);

  const updateProfile = async (data: Partial<TUserSelfPayload>) => {
    try {
      // Only allow editing of specific fields
      const allowedFields: Partial<TUserSelfPayload> = {};
      
      if (data.first_name !== undefined) allowedFields.first_name = data.first_name;
      if (data.last_name !== undefined) allowedFields.last_name = data.last_name;
      if (data.phone !== undefined) allowedFields.phone = data.phone;
      if (data.image !== undefined) allowedFields.image = data.image;

      await dispatch(updateSelfAsync(allowedFields));
      // Refresh user data after update
      await dispatch(getSelfAsync());
    } catch (error) {
      console.error('Failed to update profile:', error);
    }
  };

  if (!userProfile) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
          <p className="text-gray-600">Φόρτωση προφίλ...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto min-h-screen max-w-6xl bg-gradient-to-r from-gray-50/50 via-white to-blue-50/30 pt-4">
      <div className="relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <ProfileHeader userProfile={userProfile} updateProfile={updateProfile} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <ProfileContent userProfile={userProfile} updateProfile={updateProfile} />
        </motion.div>
      </div>
    </div>
  );
};

export default Profile;